import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
dayjs.extend(customParseFormat);
dayjs.extend(utc);

/**
 * Helper function to get the current year.
 */
export function getCurrentYear() {
    return new Date().getFullYear();
}

/**
 * Returns an object that has the years & months elapsed from the specified date until now.
 * @param sinceMonth {string|number} - Month (1-12)
 * @param sinceYear {string|number} - Year (e.g. 2022)
 * @return {{years: number, months: number, totalMonths: number, isFuture: boolean, isPast: boolean, isCurrent: boolean}|null}
 */
export function getYearsAndMonthsSinceDate(sinceYear, sinceMonth) {
    const paddedMonth = String(sinceMonth).padStart(2, '0');

    const residedSince = dayjs.utc(`${sinceYear}-${paddedMonth}-01`, 'YYYY-MM-DD', true)
    if (!residedSince.isValid()) {
        return null;
    }

    const startOfMonth = dayjs.utc().startOf('month');

    // Calculate total months difference (can be negative)
    const totalMonths = startOfMonth.diff(residedSince, 'month');
    if (totalMonths >= (120 * 12)) {
         // As far as I know, we have no inputs where 120 years difference is valid
         return null;
    }

    // Calculate years and months (negative if in future)
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;

    return {
        isFuture: totalMonths < 0,
        isPast: totalMonths > 0,
        isCurrent: totalMonths === 0,
        totalMonths,
        years,
        months
    };
}

/**
 * Returns an object that has the years & months elapsed from the specified date until now.
 *
 * @param dateObject {{year: number, month: number, day: number}} - Date Object containing {year, month, day}
 * @returns {{years: number, months: number, totalMonths: number, isFuture: boolean, isPast: boolean, isCurrent: boolean}|null}
 * e.g.
 * input
 * {
 *      year: 2022,
 *      month: 09,
 *      day: 15
 * }
 * returns:
 * {
 *      'years': {number},
 *      'months': {number},
 *      'totalMonths': {number}
 * }
 */
 export const getTimeSinceDateObject = (dateObject) => {
    if (!dateObject?.year || !dateObject?.month) {
       // I do not think defaulting to 'now' is a good idea, but that is how it was before
       // and changing it how it works now would break a ton of tests, which is not ok
       // with all the other changes that are being made.
       const date = new Date();
       return getYearsAndMonthsSinceDate(date.getFullYear(), date.getMonth() + 1);
    }
    return getYearsAndMonthsSinceDate(dateObject.year, dateObject.month);
}

export const DateInputType = {
    FULL_DATE: "MM / DD / YYYY",
    MONTH_AND_YEAR: "MM / YYYY"
}

/**
 * Returns a date string from a date object
 *
 * @param {{year: number, month: number, day: number} | null | undefined} date - Date Object containing {year, month, day}
 * @param {string} separator - Date separator. Defaults to "-"
 * @returns {string}
 * e.g.
 * input
 * {
 *      year' 2022,
 *      month: 09,
 *      day: 15
 * }
 * returns:
 * '2022-09-15'
 */
export const getDateStringFromObject = (date, separator="-") => {
   if (date?.year && date?.month && date?.day) {
      const paddedMonth = String(date.month).padStart(2, '0');
      const paddedDay = String(date.day).padStart(2, '0');
      const parsedDate = dayjs.utc(`${date.year}-${paddedMonth}-${paddedDay}`, 'YYYY-MM-DD', true)
      if (parsedDate.isValid()) {
         // don't render invalid dates
         return parsedDate.format(`YYYY${separator}MM${separator}DD`);
      }
   }
   return ""
}
